import { createSelector } from '@reduxjs/toolkit';
import type { IRootState } from '@store/slices';

export const selectMonitorState = (s: IRootState) => s.monitor;
export const selectSiteId = (s: IRootState) => s.monitor.siteId;
export const selectSnapshotStatus = (s: IRootState) => s.monitor.snapshotStatus;
export const selectStreamStatus = (s: IRootState) => s.monitor.streamStatus;
export const selectSelectedFloorId = (s: IRootState) => s.monitor.selectedFloorId;
export const selectActiveTab = (s: IRootState) => s.monitor.activeTab;
export const selectMonitorLayer = (s: IRootState) => s.monitor.layer;
export const selectActivityFilter = (s: IRootState) => s.monitor.activityFilter;
export const selectIssueFilter = (s: IRootState) => s.monitor.issueFilter;

/**
 * LeftRail filter sets. Empty array = no filter (show all). Non-empty =
 * whitelist of ids/types whose chips should remain visible across the
 * building view, floor popover, drawer, and special-area cards.
 */
export const selectSelectedProfessionIds = (s: IRootState) =>
  s.monitor.selectedProfessionIds;
export const selectSelectedManagerIds = (s: IRootState) =>
  s.monitor.selectedManagerIds;
export const selectSelectedEquipmentTypes = (s: IRootState) =>
  s.monitor.selectedEquipmentTypes;

export const selectProject = (s: IRootState) => s.monitor.project;
// Array selectors default to [] so the page renders correctly even when the
// snapshot or SSE event omits a field (Trusstor's payload shapes don't always
// include every key, e.g. dashboardData/stream nests fields under `dashboardData`).
export const selectFloors = (s: IRootState) => s.monitor.floors ?? [];
export const selectSafetyEvents = (s: IRootState) => s.monitor.safetyEvents ?? [];
export const selectManagers = (s: IRootState) => s.monitor.managers ?? [];
export const selectProfessions = (s: IRootState) => s.monitor.professions ?? [];
export const selectProfessionsWithActiveTags = (s: IRootState) =>
  s.monitor.professionsWithActiveTags ?? [];
export const selectDashboardData = (s: IRootState) => s.monitor.dashboardData;
export const selectStaticEquipment = (s: IRootState) => s.monitor.staticEquipment ?? [];
export const selectActiveEquipment = (s: IRootState) => s.monitor.activeEquipment ?? [];
export const selectUtilities = (s: IRootState) => s.monitor.utilities ?? [];
export const selectUserPermissions = (s: IRootState) => s.monitor.userPermissions;
export const selectDisplayFloorIds = (s: IRootState) => s.monitor.displayFloorIds;
export const selectStaticTags = (s: IRootState) => s.monitor.staticTags ?? [];

/**
 * True unless the user's permissions explicitly disable project-monitor
 * safety access. Conservative default — allowed when the permission shape
 * is unknown. Shared by the Safety Alerts card and the full safety view.
 */
export const selectSafetyAllowed = (s: IRootState): boolean =>
  (s.monitor.userPermissions as { projectMonitor?: { safety?: boolean } } | null)
    ?.projectMonitor?.safety !== false;

export const selectSelectedFloor = createSelector(
  [selectFloors, selectSelectedFloorId],
  (floors, selectedId) => floors.find((f) => f.floorId === selectedId) ?? null,
);
